import { useState } from 'react';
import { rotatedBounds, unionRects, type Rect } from '../../doc/geometry';
import { IconClose } from '../../ui/icons';
import { useBoard, useBoardMeta, usePane, usePaneStore } from '../context';
import { boardBounds } from './rasterize';
import { exportRect } from './exportActions';
import { useExportSettings, setExportSettings } from './exportSettings';

/** Export dialog: what to export (selection, whole board, dragged area), scale, background. */
export function ExportPanel({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const pane = usePaneStore();
  const meta = useBoardMeta();
  const selection = usePane((s) => s.selection);
  const settings = useExportSettings((s) => s);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (rect: Rect | null, label: string) => {
    if (!rect) return;
    setBusy('Rendering…');
    setError(null);
    try {
      await exportRect(board, meta.title, rect, label);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const selectionRect = () => {
    const items = [...selection].map((id) => board.index.get(id)).filter((i): i is NonNullable<typeof i> => !!i);
    return unionRects(items.map(rotatedBounds));
  };

  return (
    <aside className="panel export-panel" onPointerDown={(e) => e.stopPropagation()}>
      <div className="insp-head">
        <span>Export PNG</span>
        <button type="button" className="panel-close" aria-label="Close" onClick={onClose}>
          <IconClose />
        </button>
      </div>
      <div className="insp-body">
        <div className="insp-label">Scale</div>
        <div className="btn-row">
          {[1, 2, 4].map((s) => (
            <button key={s} type="button" className={`btn${settings.scale === s ? ' is-active' : ''}`} onClick={() => setExportSettings({ scale: s })}>
              {s}×
            </button>
          ))}
        </div>
        <label className="opt-check">
          <input type="checkbox" checked={settings.background} onChange={(e) => setExportSettings({ background: e.target.checked })} />
          Board background (off = transparent)
        </label>
        <div className="insp-label">Padding</div>
        <div className="btn-row">
          {[0, 24, 64].map((p) => (
            <button key={p} type="button" className={`btn${settings.padding === p ? ' is-active' : ''}`} onClick={() => setExportSettings({ padding: p })}>
              {p}
            </button>
          ))}
        </div>
        <div className="insp-label">Export</div>
        <div className="share-form">
          <button type="button" className="btn btn-primary" disabled={!!busy || selection.size === 0} onClick={() => run(selectionRect(), 'selection')}>
            Selection ({selection.size})
          </button>
          <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => run(boardBounds(board), 'board')}>
            Whole board
          </button>
          <button
            type="button"
            className="btn"
            disabled={!!busy}
            onClick={() => {
              pane.getState().setTool('exportArea');
              onClose();
            }}
          >
            Drag an area…
          </button>
        </div>
        {busy && <div className="insp-muted">{busy}</div>}
        {error && <div className="form-error">{error}</div>}
        <div className="insp-muted">Text items are rendered from their on-screen look; link-card images are left out.</div>
      </div>
    </aside>
  );
}
